import React, { useMemo } from 'react'
import DOMPurify from 'dompurify'
import { RoboAdvisorData } from '../../../utils/types'
import { useAdvisorCard } from './AdvisorCardContext'
import { isAdvisorData } from './advisorTypeGuards'
import { useMatchScreen } from '../../../context/MatchScreenContext'
import { trackEvent } from '../../../utils/tracking/mixpanel'
import clickTrack from '../../../utils/tracking/adClick'
import advisorLinks from '../../../utils/appconfigs/RoboLinks'
import '../../styles/AdvisorCardsModule/RoboAdvisorContent.scss'

const handleCtaClick = (pageKey: string, roboAdvisorName: string, ctaDestination: string) => {
  const eventPayload = {
    page_key: pageKey,
    'robo-advisor': roboAdvisorName
  }
  trackEvent('MatchScreen', 'robo-advisor-click', eventPayload)
  // Read the env per click rather than at module load so both branches are testable.
  const nodeEnv = typeof process !== 'undefined' && process.env ? process.env.NODE_ENV : 'production'
  if (nodeEnv !== 'production') {
    window.open(ctaDestination, '_blank', 'noopener,noreferrer')
  } else {
    clickTrack(ctaDestination, '_blank', eventPayload)
  }
}

export const RoboAdvisorContent = () => {
  const { advisorData, ctaLabels } = useAdvisorCard()
  const { state: { pageKey } } = useMatchScreen()
  const advisor = (advisorData as RoboAdvisorData).advisor
  const { name, network, id } = advisor
  // Mirrors the legacy precedence split (AdvisorsBlock): matched robos carry no
  // cta_destination, so RoboLinks wins by advisor id; fallback robos prefer the
  // cta_destination their config supplies.
  const payloadDestination = (advisor as { cta_destination?: string }).cta_destination
  const ctaDestination = isAdvisorData(advisorData)
    ? advisorLinks[id] || payloadDestination
    : payloadDestination || advisorLinks[id]

  // the network copy is stored HTML (not markdown), so sanitize it directly and
  // render it unstyled, the way AdvisorDisclosure does
  const descriptionHtml = network?.description_html
  const sanitizedDescription = useMemo(
    () => (descriptionHtml ? DOMPurify.sanitize(descriptionHtml, { USE_PROFILES: { html: true } }) : ''),
    [descriptionHtml]
  )

  return (
    <div className='robo-advisor-content'>
      {sanitizedDescription && (
        <div className='robo-advisor-content__description'>
          <p className='robo-advisor-content__label'>About {name}</p>
          <div className='robo-advisor-content__html' dangerouslySetInnerHTML={{ __html: sanitizedDescription }} />
        </div>
      )}
      {ctaDestination && (
        <span
          role='button'
          tabIndex={0}
          className='robo-advisor-content__cta'
          onClick={() => handleCtaClick(pageKey, name, ctaDestination)}
          onKeyDown={(e) => {
            // role=button must respond to Space as well as Enter.
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleCtaClick(pageKey, name, ctaDestination)
            }
          }}
        >
          {ctaLabels.roboAdvisorCTA}
        </span>
      )}
    </div>
  )
}
